import { DateTime } from 'luxon';
import moment from 'moment';

import { isNullOrUndefined } from '../helpers/helpers';
import { sortByObjProperty } from '../common/helpers';

export default {};

export const formatDate = (dt, dateBreakdown) => {
  switch (dateBreakdown) {
    case 'year':
      return dt.toFormat('y');
    case 'quarter':
      return dt.toFormat('Q q yy');
    case 'month':
      return dt.toFormat('LLL yy');
    case 'day':
    case 'week':
    default:
      return dt.toFormat('LLL d yy');
  }
};

export const getNumBuckets = ([startMoment, endMoment] = [], dateBreakdown) => {
  const startDT = DateTime.fromMillis(startMoment.valueOf()).startOf('day');
  const endDT = DateTime.fromMillis(endMoment.valueOf()).endOf('day');
  const diff = endDT.diff(startDT);
  return Math.ceil(diff.as(dateBreakdown));
};

export const getRowName = ({ breakdown = [], record = {}, fieldTitle }) => {
  const breakdownByUser = breakdown.includes('user');
  const breakdownByStatus = breakdown.includes('status');
  const breakdownByColor = breakdown.includes('color');
  const {
    status = 'Archived Status',
    user = 'Unassigned',
    title,
    color,
  } = record;
  if (title) return title;
  if (!breakdownByUser && !breakdownByStatus && !breakdownByColor) return fieldTitle;
  let base = '';
  if (breakdownByStatus) base += status;
  if (breakdownByUser) base += `${base.length > 0 ? '-' : ''}${user}`;
  if (breakdownByColor) base += `${base.length > 0 ? '-' : ''}${color ?? '#FFFFFFFF'}`;
  return base;
};

export const formatTimeInStatus = (rawValue) => {
  const value = parseFloat(rawValue);
  if (Number.isNaN(value)) return rawValue;
  // Value is in days
  if (value > 365) {
    const years = Math.floor(value / 365);
    const months = Math.floor((value - (years * 365)) / 30);
    return `${years} yr ${months}mo`;
  }
  if (value > 30) {
    const months = Math.floor(value / 30);
    const days = Math.floor(value - (months * 30));
    return `${months}mo ${days}d`;
  }
  if (value > 0) {
    return `${value.toFixed(2)}d`;
  }
  if (value > (1 / 24)) {
    const hours = value * 24;
    return `${hours.toFixed(2)}hr`;
  }
  const minutes = value * 24 * 60;
  return `${minutes.toFixed(2)}min`;
};

export const dateRangeToDayDiffs = (dateRange) => {
  if (!dateRange || dateRange.length < 2) return [];
  const [startTime, endTime] = dateRange;
  if (isNullOrUndefined(startTime)
    || Number.isNaN(startTime)
    || isNullOrUndefined(endTime)
    || Number.isNaN(endTime)
  ) return [];
  const now = DateTime.local();
  const startDT = DateTime.fromMillis(startTime).startOf('day');
  const endDT = DateTime.fromMillis(endTime).startOf('day');
  return [
    Math.floor(now.diff(startDT).as('days')),
    Math.floor(now.diff(endDT).as('days')),
  ];
};

export const dayDiffsToDateRange = (dayDiffs) => {
  if (!dayDiffs || dayDiffs.length < 2) return null;
  const [startDiff, endDiff] = dayDiffs;
  if (isNullOrUndefined(startDiff)
    || Number.isNaN(startDiff)
    || isNullOrUndefined(endDiff)
    || Number.isNaN(endDiff)
  ) return null;
  const now = DateTime.local();
  return [
    now.minus({ days: startDiff }).toMillis(),
    now.minus({ days: endDiff }).toMillis(),
  ];
};

const BREAKDOWN_KEY_TO_DATA_KEY = {
  user: 'userId',
  status: 'statusId',
  boardCard: 'cardTitle',
  boardStatus: 'boardStatus',
  hourType: 'hourType',
  division: 'divisionId',
  team: 'teamId',
  project: 'projectId',
  costcode: 'costcodeId',
};

export const TIMECARD_CUSTOM_FIELD_BREAKDOWN_PREFIX = 'timecardCustomField:';

export const isTimecardCustomFieldBreakdownKey = (key) => (
  typeof key === 'string' && key.startsWith(TIMECARD_CUSTOM_FIELD_BREAKDOWN_PREFIX)
);

export const getTimecardCustomFieldIdFromBreakdownKey = (key) => (
  isTimecardCustomFieldBreakdownKey(key)
    ? key.slice(TIMECARD_CUSTOM_FIELD_BREAKDOWN_PREFIX.length)
    : null
);

export const isEligibleTimecardCustomBreakdownField = (field) => {
  if (field?.selectedType !== 'dropdown') return false;
  const { openLimit, numAnswers = 1 } = field.configProps || {};
  return !openLimit && numAnswers === 1;
};

const mapEntityListToFilters = (items, getLabel = (item) => item?.name) => {
  if (!items?.length) return [];
  const out = [];
  items.forEach((item) => {
    const { id } = item;
    if (!id) return;
    const label = getLabel(item);
    out.push({
      text: label,
      value: id,
    });
  });
  out.sort(sortByObjProperty('text'));
  return out;
};

export const getTimecardCustomBreakdownSelectOptions = ({
  customFieldsByDivision = {},
  divisionIds = [],
}) => {
  const opts = [];
  divisionIds.forEach((divId) => {
    const { fields: sections = [] } = customFieldsByDivision[divId] || {};
    sections.forEach((section) => {
      if (section.id === 'timecard') return;
      const sectionName = section.name || '';
      (section.fields || []).forEach((field) => {
        if (!isEligibleTimecardCustomBreakdownField(field)) return;
        const { id } = field;
        if (!id) return;
        const title = field.configProps?.title;
        opts.push({
          label: `${sectionName} - ${title}`,
          value: `${TIMECARD_CUSTOM_FIELD_BREAKDOWN_PREFIX}${id}`,
        });
      });
    });
  });
  opts.sort(sortByObjProperty('label'));
  return opts;
};

export const getTimecardCustomFieldFilterSearchFilters = ({
  customFieldsByDivision = {},
  divisionIds = [],
  fieldId,
  entityMaps = {},
}) => {
  let field;
  divisionIds.forEach((divId) => {
    const { fields: sections = [] } = customFieldsByDivision[divId] || {};
    sections.forEach((section) => {
      (section.fields || []).forEach((f) => {
        if (f.id === fieldId && !field) field = f;
      });
    });
  });

  const none = { text: '(None)', value: '' };
  if (!field) return [none];

  const dataType = field.configProps?.dataType || 'Custom';

  const customOpts = (field.configProps?.customOptions || []).map(({ id, name }) => ({
    text: name,
    value: id,
  }));

  const {
    projects = [],
    costcodes = [],
    users = [],
    teams = [],
    divisionsList = [],
    customers = [],
    vendors = [],
    equipment = [],
    labels = [],
    formTemplates = [],
    scheduleCategories = [],
    buckets = [],
  } = entityMaps;

  let dynamic = [];
  switch (dataType) {
    case 'Custom':
      dynamic = customOpts;
      break;
    case 'Projects':
      dynamic = mapEntityListToFilters(projects);
      break;
    case 'Costcodes':
      dynamic = mapEntityListToFilters(costcodes);
      break;
    case 'Users':
      dynamic = mapEntityListToFilters(users);
      break;
    case 'Teams':
      dynamic = mapEntityListToFilters(teams);
      break;
    case 'Divisions':
      dynamic = mapEntityListToFilters(divisionsList);
      break;
    case 'Customers':
      dynamic = mapEntityListToFilters(customers);
      break;
    case 'Vendors':
      dynamic = mapEntityListToFilters(vendors);
      break;
    case 'Equipment':
      dynamic = mapEntityListToFilters(equipment);
      break;
    case 'Labels':
      dynamic = mapEntityListToFilters(labels, (l) => l.title);
      break;
    case 'Forms':
      dynamic = mapEntityListToFilters(formTemplates);
      break;
    case 'ScheduleCategories':
      dynamic = mapEntityListToFilters(scheduleCategories);
      break;
    case 'Buckets':
      dynamic = mapEntityListToFilters(buckets);
      break;
    default:
      dynamic = customOpts;
      break;
  }

  if (!dynamic.length) {
    return [none];
  }

  const opts = [...dynamic];
  opts.unshift(none);
  return opts;
};

export const filterData = ({
  fullData = [],
  filters = {},
  breakdown = [],
}) => {
  const bSet = new Set(breakdown);
  const filterSets = [];

  Object.keys(filters).forEach((filterKey) => {
    filterSets.push({
      filterKey,
      filterSet: new Set(filters[filterKey]),
    });
  });

  return fullData.filter((datum) => (
    filterSets.every(({ filterKey, filterSet }) => {
      if (!bSet.has(filterKey) || filterSet.size === 0) return true;
      if (isTimecardCustomFieldBreakdownKey(filterKey)) {
        const v = datum[filterKey];
        return filterSet.has(v);
      }
      const dataKey = BREAKDOWN_KEY_TO_DATA_KEY[filterKey];
      return filterSet.has(datum[dataKey]);
    })
  ));
};

export const getValidCharts = (type) => {
  switch (type) {
    case 'dropdown':
    case 'yes-no':
      return new Set(['pie', 'stackedLine']);
    case 'conversion':
    case 'timeInStatus':
    case 'numberOfCards':
    case 'numberOfSubmissions':
    case 'newCardsInStatus':
    case 'hoursWorked':
      return new Set(['line', 'bar', 'number', 'table']);
    case 'text':
    case 'table':
    case 'calculation':
    default:
      return new Set(['line', 'bar', 'number', 'table', 'list']);
  }
};

export const rangePresetToTimestamps = (preset) => {
  const now = moment().valueOf();
  switch (preset) {
    case 'This Week': return [moment().startOf('week').valueOf(), now];
    case 'This Month': return [moment().startOf('month').valueOf(), now];
    case 'This Quarter': return [moment().startOf('quarter').valueOf(), now];
    case 'This Year': return [moment().startOf('year').valueOf(), now];
    case 'Last 7 Days': return [moment().subtract(6, 'days').valueOf(), now];
    case 'All Time': return [moment().year(2018).startOf('year').valueOf(), now];
    case 'Last 30 Days':
    default:
      return [moment().subtract(29, 'days').valueOf(), now];
  }
};

export const getDateRangeFromConfig = ({ datePreset, dateRange }) => {
  if (datePreset) {
    const [startTime, endTime] = rangePresetToTimestamps(datePreset);
    return [
      moment(startTime),
      moment(endTime),
    ];
  }
  if (!dateRange || dateRange.length < 2) return null;
  return [
    moment(dateRange[0]),
    moment(dateRange[1]),
  ];
};

export const canBreakdownByField = ({
  configProps: {
    openLimit,
    numAnswers = 1,
  } = {},
  selectedType,
}) => {
  if (selectedType !== 'yes-no' && selectedType !== 'dropdown') return false;
  if (selectedType === 'yes-no') return true;
  return !openLimit && numAnswers === 1;
};

export const getFieldOptions = ({
  sectionName,
  field: {
    id: fieldId,
    configProps: {
      title: fieldName,
      columns = [],
    } = {},
    selectedType,
  } = {},
}) => {
  if (selectedType === 'table') {
    return columns.map((col) => (
      { label: `${sectionName} - ${fieldName} - ${col.name}`, value: `${fieldId}/${col.key}` }
    ));
  }
  return [{ label: `${sectionName} - ${fieldName}`, value: fieldId }];
};

export const getFieldType = (fieldId, fieldMap = {}) => {
  if (
    fieldId === 'numberOfCards'
    || fieldId === 'timeInStatus'
    || fieldId === 'conversion'
    || fieldId === 'newCardsInStatus'
    || fieldId === 'numberOfSubmissions'
    || fieldId === 'hoursWorked'
    || !fieldId
  ) return fieldId;
  if (fieldId.includes('/')) {
    const [realFieldId] = fieldId.split('/');
    return (fieldMap[realFieldId] ?? {}).selectedType;
  }
  return (fieldMap[fieldId] ?? {}).selectedType;
};
